import { useCallback, useEffect, useRef } from 'react'
import { useEdgesState, useNodesState } from '@xyflow/react'
import { unstable_batchedUpdates } from 'react-dom'
import { JourneyEdge, JourneyNode, JourneyStepData } from '../types/journey-nodes'
import { positionNodes } from '../helpers'
import { injectPerfCardNodes, isPerfCardId, isPerfCardEdgeId } from './perf-card-injection'
import { isConvergencePlaceholderNode } from './node-dimensions'
import { StepType } from '@pushly/models/lib/enums/journeys'
import { isVariantSplitStep } from '@pushly/models/lib/structs/journeys'

type UseRenderedJourneyGraphParams = {
    canonicalNodes: JourneyNode[]
    canonicalEdges: JourneyEdge[]
}

type UseRenderedJourneyGraphResult = {
    renderNodes: JourneyNode[]
    renderEdges: JourneyEdge[]
    onRenderNodesChange: ReturnType<typeof useNodesState<JourneyNode>>[2]
    onRenderEdgesChange: ReturnType<typeof useEdgesState<JourneyEdge>>[2]
    clearRenderGraph: () => void
}

type GraphFingerprints = {
    nodes: string
    edges: string
    measurements: string
}

function getNodeStructureFingerprint(nodes: JourneyNode[]): string {
    return nodes
        .map((n) => {
            // A variant split's per-variant perf card is a SYNTHETIC node (no canonical
            // counterpart), so Effect 2 never reconciles it — its header (variant name +
            // split %) is only ever set at injection time, inside the layout pass. Those
            // values aren't topology, so fold them into the structure fingerprint here;
            // otherwise a name/percentage-only edit (Update Step) changes neither
            // fingerprint, the layout never re-runs, and the card shows a stale value.
            if (n.data?.step?.type === StepType.VARIANT_SPLIT && isVariantSplitStep(n.data.step)) {
                const branchSig = (n.data.step.configuration.branches ?? [])
                    .map((b) => `${b.key}@${b.label}@${b.kind === 'variant' ? b.percentage : ''}`)
                    .join('|')
                return `${n.id}:${n.type}:[${branchSig}]`
            }
            return `${n.id}:${n.type}`
        })
        .sort()
        .join(',')
}

function getEdgeStructureFingerprint(edges: JourneyEdge[]): string {
    return edges
        .map((e) => {
            const branchKey = (e.data as { branchKey?: string } | undefined)?.branchKey ?? ''
            return `${e.id}:${e.type}:${e.source}:${e.sourceHandle ?? ''}->${e.target}:${e.targetHandle ?? ''}:${branchKey}`
        })
        .sort()
        .join(',')
}

function getMeasurementsFingerprint(nodes: JourneyNode[]): string {
    return nodes
        // Synthetic nodes (per-variant perf cards, convergence dots) are laid out
        // at FIXED pre-measure dimensions (see node-dimensions.ts), so their
        // DOM-measured size never feeds ELK and must be excluded here. They are
        // re-injected on every layout pass WITHOUT a `measured` value (0x0), then
        // React Flow measures them (e.g. 250x72); including them made this
        // fingerprint flip 0x0 ↔ measured every frame, re-running ELK in an
        // infinite loop that churned the edges/(+) overlays and made them
        // un-clickable. Only real (canonical) nodes — whose measured
        // size actually drives ELK — belong in the fingerprint.
        .filter((n) => !isPerfCardId(n.id) && !isConvergencePlaceholderNode(n))
        .map((n) => `${n.id}:${n.measured?.width ?? 0}x${n.measured?.height ?? 0}`)
        .sort()
        .join(',')
}

function mergeMeasuredNodeDimensions(nodes: JourneyNode[], renderNodes: JourneyNode[]): JourneyNode[] {
    const measuredById = new Map(renderNodes.map((node) => [node.id, node.measured]))

    return nodes.map((node) => {
        const measured = measuredById.get(node.id)
        if (!measured?.width || !measured?.height) return node
        if (node.measured?.width === measured.width && node.measured?.height === measured.height) return node
        return { ...node, measured }
    })
}

function reconcileRenderNode(renderNode: JourneyNode, canonicalNode: JourneyNode): JourneyNode {
    const existingElkPorts = renderNode.data.uiMeta?.elkPortPositions
    const canonicalUiMeta = canonicalNode.data.uiMeta ?? {}
    const renderUiMeta = renderNode.data.uiMeta ?? {}
    const { elkPortPositions: _ports, ...renderUiMetaSansElk } = renderUiMeta

    const sameCoreNodeData =
        renderNode.type === canonicalNode.type
        && renderNode.data.step === canonicalNode.data.step
        && JSON.stringify(renderUiMetaSansElk) === JSON.stringify(canonicalUiMeta)

    if (sameCoreNodeData) {
        return renderNode
    }

    const nextUiMeta = existingElkPorts
        ? { ...canonicalUiMeta, elkPortPositions: existingElkPorts }
        : canonicalUiMeta
    const nextData = new JourneyStepData({
        step: canonicalNode.data.step,
        uiMeta: nextUiMeta,
    })

    return {
        ...renderNode,
        type: canonicalNode.type,
        data: nextData,
    }
}

function reconcileRenderEdge(renderEdge: JourneyEdge, canonicalEdge: JourneyEdge): JourneyEdge {
    const existingSections = (renderEdge.data as { elkSections?: unknown } | undefined)?.elkSections
    const canonicalData = (canonicalEdge.data ?? {}) as Record<string, unknown>
    const renderData = (renderEdge.data ?? {}) as Record<string, unknown>
    const { elkSections: _sections, ...renderCoreData } = renderData

    const sameCoreEdgeData =
        renderEdge.type === canonicalEdge.type
        && renderEdge.source === canonicalEdge.source
        && renderEdge.target === canonicalEdge.target
        && renderEdge.sourceHandle === canonicalEdge.sourceHandle
        && renderEdge.targetHandle === canonicalEdge.targetHandle
        && JSON.stringify(renderEdge.markerStart ?? null) === JSON.stringify(canonicalEdge.markerStart ?? null)
        && JSON.stringify(renderEdge.markerEnd ?? null) === JSON.stringify(canonicalEdge.markerEnd ?? null)
        && JSON.stringify(renderCoreData) === JSON.stringify(canonicalData)

    if (sameCoreEdgeData) {
        return renderEdge
    }

    const nextData = existingSections
        ? { ...(canonicalData as object), elkSections: existingSections }
        : canonicalData

    return {
        ...renderEdge,
        ...canonicalEdge,
        data: nextData,
    } as JourneyEdge
}

export function useRenderedJourneyGraph({
    canonicalNodes,
    canonicalEdges,
}: UseRenderedJourneyGraphParams): UseRenderedJourneyGraphResult {
    const [renderNodes, setRenderNodes, onRenderNodesChange] = useNodesState<JourneyNode>([])
    const [renderEdges, setRenderEdges, onRenderEdgesChange] = useEdgesState<JourneyEdge>([])

    const previousFingerprintsRef = useRef<GraphFingerprints>({
        nodes: '',
        edges: '',
        measurements: '',
    })
    const measurementFrameRef = useRef<number | null>(null)
    const layoutRequestSeqRef = useRef(0)
    const layoutInFlightRef = useRef(false)

    const clearRenderGraph = useCallback(() => {
        unstable_batchedUpdates(() => {
            setRenderNodes([])
            setRenderEdges([])
        })
    }, [])

    const mergeMeasuredNodes = useCallback((nodes: JourneyNode[]): JourneyNode[] => {
        return mergeMeasuredNodeDimensions(nodes, renderNodes)
    }, [renderNodes])

    // Effect 1: schedule ELK layout when canonical structure or measured dimensions change.
    useEffect(() => {
        if (!canonicalNodes.length || !canonicalEdges.length) {
            if (measurementFrameRef.current) {
                cancelAnimationFrame(measurementFrameRef.current)
                measurementFrameRef.current = null
            }
            layoutInFlightRef.current = false
            return
        }

        const currentFingerprints: GraphFingerprints = {
            nodes: getNodeStructureFingerprint(canonicalNodes),
            edges: getEdgeStructureFingerprint(canonicalEdges),
            measurements: getMeasurementsFingerprint(renderNodes),
        }

        const previous = previousFingerprintsRef.current
        const structureChanged =
            currentFingerprints.nodes !== previous.nodes
            || currentFingerprints.edges !== previous.edges
        const measurementsChanged = currentFingerprints.measurements !== previous.measurements

        if (!structureChanged && !measurementsChanged) {
            return
        }

        previousFingerprintsRef.current = currentFingerprints

        if (measurementFrameRef.current) {
            cancelAnimationFrame(measurementFrameRef.current)
            measurementFrameRef.current = null
        }

        const requestSeq = ++layoutRequestSeqRef.current
        const runLayout = async () => {
            const layoutInputNodes = mergeMeasuredNodes(canonicalNodes)
            // Inject rendered-only per-variant perf cards before ELK so they are
            // laid out as real lane-head nodes. They never touch canonical state —
            // this clone is layout/render input only.
            const { nodes: injectedNodes, edges: injectedEdges } = injectPerfCardNodes(
                layoutInputNodes,
                canonicalEdges,
            )
            const { nodes: updatedNodes, edges: updatedEdges } = await positionNodes(injectedNodes, injectedEdges)
            if (requestSeq !== layoutRequestSeqRef.current) return

            layoutInFlightRef.current = false
            unstable_batchedUpdates(() => {
                setRenderNodes(updatedNodes)
                setRenderEdges(updatedEdges)
            })
        }

        layoutInFlightRef.current = true

        if (structureChanged) {
            void runLayout()
        } else {
            measurementFrameRef.current = window.requestAnimationFrame(() => {
                measurementFrameRef.current = null
                void runLayout()
            })
        }

        return () => {
            if (measurementFrameRef.current) {
                cancelAnimationFrame(measurementFrameRef.current)
                measurementFrameRef.current = null
                layoutInFlightRef.current = false
            }
        }
    }, [canonicalNodes, canonicalEdges, renderNodes, mergeMeasuredNodes])

    // Effect 2: when topology is stable, sync canonical node/edge data into the render graph
    // while preserving ELK metadata (ports/sections) from the rendered graph.
    useEffect(() => {
        if (!canonicalNodes.length || !canonicalEdges.length || layoutInFlightRef.current) {
            return
        }

        // The rendered graph carries extra synthetic perf-card nodes and their
        // card-OUT edges that have no canonical counterpart. Exclude only those
        // from the topology-stability length comparison. The ORIGINAL branch edges are
        // retargeted onto the perf-card (so `e.target` is a perf-card id) but remain
        // CANONICAL edges with their canonical ids — they must NOT be counted as
        // synthetic, or the count is inflated by one-per-branch and the equality check
        // below is never satisfied, permanently disabling data-sync on split journeys.
        const syntheticNodeCount = renderNodes.filter((n) => isPerfCardId(n.id)).length
        const syntheticEdgeCount = renderEdges.filter((e) => isPerfCardEdgeId(e.id)).length

        if (
            canonicalNodes.length !== renderNodes.length - syntheticNodeCount
            || canonicalEdges.length !== renderEdges.length - syntheticEdgeCount
        ) {
            return
        }

        const currentFingerprints: GraphFingerprints = {
            nodes: getNodeStructureFingerprint(canonicalNodes),
            edges: getEdgeStructureFingerprint(canonicalEdges),
            measurements: getMeasurementsFingerprint(renderNodes),
        }
        const previous = previousFingerprintsRef.current
        const structureChanged =
            currentFingerprints.nodes !== previous.nodes
            || currentFingerprints.edges !== previous.edges
        const measurementsChanged = currentFingerprints.measurements !== previous.measurements

        if (structureChanged || measurementsChanged) {
            return
        }

        const canonicalNodesById = new Map(canonicalNodes.map((node) => [node.id, node]))
        const canonicalEdgesById = new Map(canonicalEdges.map((edge) => [edge.id, edge]))

        unstable_batchedUpdates(() => {
            setRenderNodes((prev) => {
                let changed = false
                const next = prev.map((renderNode) => {
                    const canonicalNode = canonicalNodesById.get(renderNode.id)
                    if (!canonicalNode) return renderNode

                    const reconciledNode = reconcileRenderNode(renderNode, canonicalNode)
                    if (reconciledNode !== renderNode) {
                        changed = true
                    }
                    return reconciledNode
                })
                return changed ? next : prev
            })

            setRenderEdges((prev) => {
                let changed = false
                const next = prev.map((renderEdge) => {
                    const canonicalEdge = canonicalEdgesById.get(renderEdge.id)
                    if (!canonicalEdge) return renderEdge

                    const reconciledEdge = reconcileRenderEdge(renderEdge, canonicalEdge)
                    if (reconciledEdge !== renderEdge) {
                        changed = true
                    }
                    return reconciledEdge
                })
                return changed ? next : prev
            })
        })
    }, [canonicalNodes, canonicalEdges, renderNodes, renderEdges])

    useEffect(() => {
        let resizeTimeout: number | null = null

        const handleResize = () => {
            if (resizeTimeout) {
                clearTimeout(resizeTimeout)
            }

            resizeTimeout = window.setTimeout(async () => {
                if (canonicalNodes?.length && canonicalEdges?.length) {
                    const requestSeq = ++layoutRequestSeqRef.current
                    layoutInFlightRef.current = true
                    const layoutInputNodes = mergeMeasuredNodes(canonicalNodes)
                    const { nodes: injectedNodes, edges: injectedEdges } = injectPerfCardNodes(
                        layoutInputNodes,
                        canonicalEdges,
                    )
                    const { nodes: updatedNodes, edges: updatedEdges } = await positionNodes(injectedNodes, injectedEdges)
                    if (requestSeq !== layoutRequestSeqRef.current) return

                    layoutInFlightRef.current = false
                    unstable_batchedUpdates(() => {
                        setRenderNodes(updatedNodes)
                        setRenderEdges(updatedEdges)
                    })
                }
            }, 100)
        }

        window.addEventListener('resize', handleResize)
        return () => {
            window.removeEventListener('resize', handleResize)
            if (resizeTimeout) {
                clearTimeout(resizeTimeout)
            }
        }
    }, [canonicalNodes, canonicalEdges, mergeMeasuredNodes])

    return {
        renderNodes,
        renderEdges,
        onRenderNodesChange,
        onRenderEdgesChange,
        clearRenderGraph,
    }
}
